import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface GalleryImage {
  id: string;
  image_url: string;
  title: string;
  description: string;
  created_at: string;
  user_id: string;
  partner_id?: string;
  profiles?: {
    name: string;
  };
}

const partners = [
  { id: '1', name: '박성숙 - 남도음식전문가' },
  { id: '2', name: '복영문 - 제이앤비농장' },
  { id: '3', name: '정종권 - 봄햇살케어팜' },
  { id: '4', name: '최현귀 - 두울리' },
  { id: '5', name: '박지영 - 세모실커피농장' },
  { id: '6', name: '김은정 - 한옥펜션' },
  { id: '7', name: '손현숙 - 하루애꽃' }
];

export default function GalleryPage() {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<string>('전체');
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    partner_id: '',
    file: null as File | null
  });
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    loadGalleryImages();
  }, []);

  const loadGalleryImages = async () => {
    try {
      const { data, error } = await supabase
        .from('gallery')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // 프로필 정보를 별도로 가져오기
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(img => img.user_id))];
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', userIds);
        
        // 프로필 정보를 매핑
        const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
        const imagesWithProfiles = data.map(img => ({
          ...img,
          profiles: profilesMap.get(img.user_id) || null
        }));
        
        setImages(imagesWithProfiles);
      } else {
        setImages([]);
      }
    } catch (error) {
      console.error('갤러리 이미지 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = selectedPartner === '전체' 
    ? images 
    : images.filter(img => img.partner_id === selectedPartner);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadForm({ ...uploadForm, file: e.target.files[0] });
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !uploadForm.file) return;

    setUploading(true);
    try {
      // 파일 업로드
      const fileExt = uploadForm.file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `gallery/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, uploadForm.file);

      if (uploadError) throw uploadError;

      // 공개 URL 가져오기
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      // 데이터베이스에 저장
      const { error: dbError } = await supabase
        .from('gallery')
        .insert({
          image_url: publicUrl,
          title: uploadForm.title,
          description: uploadForm.description,
          partner_id: uploadForm.partner_id || null,
          user_id: user.id
        });

      if (dbError) throw dbError;

      // 성공 후 초기화
      setUploadForm({ title: '', description: '', partner_id: '', file: null });
      setShowUploadModal(false);
      loadGalleryImages();
    } catch (error) {
      console.error('업로드 오류:', error);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId: string, imageUrl: string) => {
    if (!confirm('이 이미지를 삭제하시겠습니까?')) return;

    try {
      // 스토리지에서 파일 삭제
      const filePath = imageUrl.split('/').slice(-2).join('/');
      await supabase.storage.from('images').remove([filePath]);

      // 데이터베이스에서 삭제
      const { error } = await supabase
        .from('gallery')
        .delete()
        .eq('id', imageId);

      if (error) throw error;

      loadGalleryImages();
      setSelectedImage(null);
    } catch (error) {
      console.error('삭제 오류:', error);
      alert('이미지 삭제에 실패했습니다.');
    }
  };

  const getPartnerName = (partnerId?: string) => {
    if (!partnerId) return '전체';
    const partner = partners.find(p => p.id === partnerId);
    return partner ? partner.name : '전체';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
        <Header />
        <div className="pt-20 pb-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-emerald-600 animate-spin mb-4"></i>
            <p className="text-gray-600">갤러리를 불러오는 중...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <Header />
      
      <main className="pt-20 pb-16">
        <div className="container mx-auto px-4">
          {/* 헤더 */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">갤러리</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              고흥꿈트리 치유마을의 아름다운 순간들을 함께 나눠요
            </p>
          </div>

          {/* 업체별 필터 탭 */}
          <div className="mb-8">
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setSelectedPartner('전체')}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  selectedPartner === '전체'
                    ? 'bg-emerald-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 shadow-md'
                }`}
              >
                전체 ({images.length})
              </button>
              {partners.map((partner) => {
                const count = images.filter(img => img.partner_id === partner.id).length;
                return (
                  <button
                    key={partner.id}
                    onClick={() => setSelectedPartner(partner.id)}
                    className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      selectedPartner === partner.id
                        ? 'bg-emerald-600 text-white shadow-lg'
                        : 'bg-white text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 shadow-md'
                    }`}
                  >
                    {partner.name} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          {/* 업로드 버튼 */}
          {user && (
            <div className="flex justify-end mb-8">
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2"
              >
                <i className="ri-upload-2-line"></i>
                <span>사진 올리기</span>
              </button>
            </div>
          )}

          {/* 갤러리 그리드 */}
          {filteredImages.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-image-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">
                {selectedPartner === '전체' 
                  ? '아직 등록된 사진이 없습니다.' 
                  : '이 업체의 사진이 아직 없습니다.'}
              </p>
              {user && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="mt-6 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  첫 번째 사진 올리기
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredImages.map((image) => (
                <div
                  key={image.id}
                  onClick={() => setSelectedImage(image)}
                  className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                >
                  <div className="w-full h-64">
                    <img
                      src={image.image_url}
                      alt={image.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-4">
                    {image.partner_id && (
                      <span className="inline-block bg-emerald-100 text-emerald-700 text-xs px-3 py-1 rounded-full mb-2 font-semibold">
                        {getPartnerName(image.partner_id)}
                      </span>
                    )}
                    <h3 className="font-semibold text-gray-900 mb-1 truncate">{image.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{image.description}</p>
                    <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                      <span>{image.profiles?.name || '익명'}</span>
                      <span>{new Date(image.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* 이미지 상세 모달 */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-y-auto w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title}
                className="w-full max-h-[60vh] object-contain bg-gray-100"
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="p-6">
              {selectedImage.partner_id && (
                <span className="inline-block bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full mb-4 font-semibold">
                  {getPartnerName(selectedImage.partner_id)}
                </span>
              )}
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedImage.title}</h2>
              <p className="text-gray-600 mb-4">{selectedImage.description}</p>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center space-x-2 text-gray-600">
                  <i className="ri-user-line"></i>
                  <span>{selectedImage.profiles?.name || '익명'}</span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-500">
                    {new Date(selectedImage.created_at).toLocaleDateString()}
                  </span>
                  {user && user.id === selectedImage.user_id && (
                    <button
                      onClick={() => handleDelete(selectedImage.id, selectedImage.image_url)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      삭제
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 업로드 모달 */}
      {showUploadModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowUploadModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">사진 올리기</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  업체 선택
                </label>
                <select
                  value={uploadForm.partner_id}
                  onChange={(e) => setUploadForm({ ...uploadForm, partner_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">전체 (업체 선택 안 함)</option>
                  {partners.map((partner) => (
                    <option key={partner.id} value={partner.id}>
                      {partner.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  제목
                </label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  설명
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  rows={3}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  이미지 파일
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={uploading}
                className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {uploading ? '업로드 중...' : '업로드'}
              </button>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
